import { useEffect } from 'react'

import { useQueryStateContext } from '../contexts/QueryStateContext'

type SetFunctionParam<T> = T | undefined | null | ((prev: T) => T)

export function useQueryState<
  T extends string | number | boolean = string,
  I extends T = T
>(
  paramName: string,
  initialValue?: I,
  options: {
    typeConversion: 'string' | 'number' | 'boolean'
  } = {
    typeConversion: 'string'
  }
): [T | undefined, (param: SetFunctionParam<T>) => void] {
  const [searchParams, setSearchParams] = useQueryStateContext()

  useEffect(() => {
    if (
      initialValue === null ||
      initialValue === undefined ||
      initialValue === '' ||
      searchParams.get(paramName)
    )
      return
    setSearchParams((searchParams) => {
      searchParams.set(paramName, initialValue.toString())
      return searchParams
    })
  }, [])

  const currentValue = searchParams.get(paramName)
  const convertedValue = {
    string: !currentValue ? undefined : currentValue,
    number: !currentValue
      ? undefined
      : Number.isNaN(+currentValue)
        ? +(initialValue ?? 0)
        : +currentValue,
    boolean:
      currentValue === 'true'
        ? true
        : currentValue === 'false'
          ? false
          : undefined
  }

  const finalValue = convertedValue[options.typeConversion] as T

  function setParam(p: SetFunctionParam<T>) {
    const param = typeof p === 'function' ? p(finalValue) : p
    setSearchParams((searchParams) => {
      param === undefined || param === null || param === ''
        ? searchParams.delete(paramName)
        : searchParams.set(paramName, param.toString())
      return searchParams
    })
  }

  return [finalValue, setParam] as const
}
